import React, { useCallback, useState, useRef } from 'react';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'react-final-form';
import pick from 'lodash/pick';

// Constants
import TrackingConstants from 'Shared/constants/Tracking.constants';
import { GoalWhiteList } from './constants';

// Types
import { ReminderType, Steps } from './types';
import { GoalItem, GoalIcons } from 'Shared/constants/types';

// Hooks
import { useResponsiveScreenWidth } from 'Wpds/hooks/useResponsiveScreenWidth';
import { useToggle } from 'Shared/utils/hooks';

// Actions
import { useInviteToWorkspace } from 'Shared/actions/inviteToWorkspace';
import { createGoalV2 } from 'Shared/actions/Goals.actions';

// Components
import { WpModal, WpModalBody, WpModalFooter, WpModalHeader } from 'Wpds/components/modal';
import { WpButtonGroup, WpButton } from 'Wpds/components/button';
import { VisuallyHidden } from 'Wpds/components/visually-hidden/VisuallyHidden';
import PrivacyAndRemindersV2 from './PrivacyAndReminders/PrivacyAndRemindersV2';
import NameAndDescription from './NameAndDescription/NameAndDescription';
import GoalTracking from './GoalTracking/GoalTracking';

// Utils
import { generateGoalV2 } from './utils';
import { showNotificationError } from 'Shared/helpers';

import './CreateGoalModal.scss';

type Props = {
  goalToClone?: GoalItem;
  parentId?: number;
  isOpen: boolean;
  toggleIsOpen: () => void;
};

function CreateGoalModal({ goalToClone, isOpen, parentId, toggleIsOpen }: Props) {
  const history = useHistory();
  const dispatch = useDispatch();
  const submitBtnRef = useRef(null);
  const { isMedium } = useResponsiveScreenWidth({ isAtLeast: true });

  const [isSubmitting, toggleIsSubmitting] = useToggle(false);

  const workspaces = useSelector(state => state.workspaces);
  const users = useSelector(state => state.members);

  const [step, setStep] = useState(Steps.nameAndDescription);

  const redirectToGoalDetailsPage = useCallback(
    slug => {
      history.push(`/goals/${slug}`);
    },
    [history]
  );

  const createNewGoal = useCallback(
    values => {
      toggleIsSubmitting();
      return dispatch(createGoalV2({ goal: generateGoalV2(values) })).then(response => {
        const newGoal = response.data.attributes;
        toggleIsSubmitting();
        toggleIsOpen();
        redirectToGoalDetailsPage(newGoal.slug);
      });
    },
    [dispatch, redirectToGoalDetailsPage]
  );
  const [inviteToWorkspace] = useInviteToWorkspace({
    inviteMethod: TrackingConstants.INVITE_METHOD_TYPES.CREATE_UPDATE_GOAL,
  });

  const handleAddUserToWorkspace = async (assigneeId, workspaceId) => {
    const assignee = users.find(u => u.id === assigneeId);
    try {
      await inviteToWorkspace({
        workspaceId: workspaceId,
        emails: [assignee.email],
      });
    } catch (e) {
      showNotificationError(e);
    }
  };

  const inviteAssigneeToGroupWorkspace = useCallback(
    ({ workspaceId, assigneeId }) => {
      const groupWorkspace = workspaces.find(({ id }) => workspaceId === id);
      const groupWorkspaceUsers = groupWorkspace.users;
      const assigneeUser = groupWorkspaceUsers.find(({ id }) => assigneeId === id);
      if (assigneeUser) return false;
      handleAddUserToWorkspace(assigneeId, workspaceId);
      return true;
    },
    [workspaces]
  );

  const handleSubmit = useCallback(
    values => {
      if (step === Steps.nameAndDescription) {
        return setStep(Steps.objective);
      }
      if (step === Steps.objective) {
        return setStep(Steps.privacyAndReminders);
      }
      if (step === Steps.privacyAndReminders) {
        inviteAssigneeToGroupWorkspace(values);
        return createNewGoal(values);
      }
      return values;
    },
    [step, inviteAssigneeToGroupWorkspace, dispatch, createNewGoal]
  );

  const handleNextClick = useCallback(() => {
    submitBtnRef.current.click();
  }, []);

  const handleBackButtonClick = useCallback(() => {
    if (step === Steps.privacyAndReminders) setStep(Steps.objective);
    if (step === Steps.objective) setStep(Steps.nameAndDescription);
  }, [step, setStep]);

  const handleCloseClick = () => {
    setStep(Steps.nameAndDescription);
    toggleIsOpen();
  };

  const initialValues = {
    ...pick(
      {
        ...goalToClone,
        assigneeId: goalToClone?.assigneeId,
        parentId: goalToClone?.parentId || (parentId ? Number(parentId) : null),
        description: goalToClone?.description || '',
        icon: goalToClone?.icon || GoalIcons.ice_cream,
        privacy: goalToClone ? (goalToClone.private ? 'private' : 'public') : 'public',
        workspaceId: goalToClone?.workspaceSlug,
      },
      GoalWhiteList
    ),
    reminder: ReminderType[goalToClone?.reminderSetting] || ReminderType.onceAWeek,
  };

  const baseClassName = 'CreateGoalModal';

  return (
    <WpModal
      isOpen={isOpen}
      size={isMedium ? 'lg' : 'full'}
      onToggle={handleCloseClick}
      dismissOnBackdrop={false}
      zIndex={1050}
    >
      <WpModalHeader>{goalToClone ? 'Duplicate Goal' : 'Create Goal'}</WpModalHeader>
      <WpModalBody padding={0}>
        <div className={baseClassName}>
          <Form initialValues={initialValues} onSubmit={handleSubmit}>
            {({ handleSubmit, submitError }) => (
              <form onSubmit={handleSubmit}>
                {step === Steps.nameAndDescription && <NameAndDescription />}
                {step === Steps.objective && <GoalTracking />}
                {step === Steps.privacyAndReminders && (
                  <PrivacyAndRemindersV2 workspaceSlug={goalToClone?.workspaceSlug} />
                )}
                <VisuallyHidden>
                  <WpButton size="md" btnType="submit" ref={submitBtnRef} qaTest="btn-create-goal-submit-form">
                    submit
                  </WpButton>
                </VisuallyHidden>
                {submitError && <>error</>}
              </form>
            )}
          </Form>
        </div>
      </WpModalBody>
      <WpModalFooter>
        <WpButtonGroup>
          {step !== Steps.nameAndDescription && (
            <WpButton variant="tertiary" square onClick={handleBackButtonClick} disabled={isSubmitting}>
              Back
            </WpButton>
          )}
          <WpButton square onClick={handleNextClick} loading={isSubmitting} qaTest="btn-create-goal-next-btn">
            {step === Steps.privacyAndReminders ? 'Create Goal' : 'Next'}
          </WpButton>
        </WpButtonGroup>
      </WpModalFooter>
    </WpModal>
  );
}

export default CreateGoalModal;
